import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PrivacyControlsTab = ({ privacySettings, onSave }) => {
  const [settings, setSettings] = useState({
    privacyLevel: privacySettings?.privacyLevel || 'high',
    photoBlur: privacySettings?.photoBlur || true,
    blurIntensity: privacySettings?.blurIntensity || 'high',
    hideFace: privacySettings?.hideFace || false,
    profileVisibility: privacySettings?.profileVisibility || 'anonymous',
    messageAccess: privacySettings?.messageAccess || 'verified-only',
    showOnlineStatus: privacySettings?.showOnlineStatus || false,
    showLastActive: privacySettings?.showLastActive || false,
    allowProfileViews: privacySettings?.allowProfileViews || true
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const privacyLevelOptions = [
    { 
      value: 'high', 
      label: 'High Privacy',
      description: 'Maximum anonymity - Profile completely anonymous'
    },
    { 
      value: 'medium', 
      label: 'Medium Privacy',
      description: 'Balanced - Limited profile information visible'
    },
    { 
      value: 'low', 
      label: 'Low Privacy',
      description: 'Open - Most profile details visible'
    }
  ];

  const blurIntensityOptions = [
    { value: 'high', label: 'High Blur', description: 'Maximum photo protection' },
    { value: 'medium', label: 'Medium Blur', description: 'Moderate protection' },
    { value: 'low', label: 'Low Blur', description: 'Minimal blur effect' }
  ];

  const visibilityOptions = [
    { value: 'anonymous', label: 'Anonymous', description: 'No personal details shown' },
    { value: 'limited', label: 'Limited', description: 'Basic info only' },
    { value: 'public', label: 'Public', description: 'Full profile visible' }
  ];

  const messageAccessOptions = [
    { value: 'verified-only', label: 'Verified Users Only', description: 'Only verified members can message' },
    { value: 'premium-only', label: 'Premium Members Only', description: 'Only premium subscribers' },
    { value: 'everyone', label: 'Everyone', description: 'All users can message' }
  ];

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      onSave(settings);
      setIsSaving(false);
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    }, 1500);
  };

  const getPrivacyLevelColor = () => {
    switch (settings?.privacyLevel) {
      case 'high': return 'text-success';
      case 'medium': return 'text-warning';
      case 'low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getPrivacyLevelIcon = () => {
    switch (settings?.privacyLevel) {
      case 'high': return 'ShieldCheck';
      case 'medium': return 'Shield';
      case 'low': return 'ShieldAlert';
      default: return 'Shield';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 bg-primary/10 rounded-lg border border-primary/20">
        <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-caption font-semibold text-primary mb-1">Privacy Protection Active</h3>
          <p className="text-xs text-muted-foreground">
            Your privacy settings control how others see and interact with your profile. Higher privacy levels provide better anonymity.
          </p>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${settings?.privacyLevel === 'high' ? 'bg-success/10' : settings?.privacyLevel === 'medium' ? 'bg-warning/10' : 'bg-error/10'}`}>
              <Icon name={getPrivacyLevelIcon()} size={24} color={`var(--color-${settings?.privacyLevel === 'high' ? 'success' : settings?.privacyLevel === 'medium' ? 'warning' : 'error'})`} />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold">Privacy Level</h3>
              <p className={`text-sm font-medium ${getPrivacyLevelColor()}`}>
                Currently: {privacyLevelOptions?.find(opt => opt?.value === settings?.privacyLevel)?.label}
              </p>
            </div>
          </div>
          <Select
            label="Select Privacy Level"
            options={privacyLevelOptions}
            value={settings?.privacyLevel}
            onChange={(value) => handleSettingChange('privacyLevel', value)}
            description="This affects all privacy-related settings"
          />
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Photo Privacy</h3>
          <div className="space-y-4">
            <Checkbox
              label="Enable Photo Blur"
              description="Blur your photos to protect your identity"
              checked={settings?.photoBlur}
              onChange={(e) => handleSettingChange('photoBlur', e?.target?.checked)}
            />

            {settings?.photoBlur && (
              <div className="ml-6 pl-4 border-l-2 border-primary/20">
                <Select
                  label="Blur Intensity"
                  options={blurIntensityOptions}
                  value={settings?.blurIntensity}
                  onChange={(value) => handleSettingChange('blurIntensity', value)}
                />
              </div>
            )}

            <Checkbox
              label="Hide Face Recognition"
              description="Prevent face detection in your photos"
              checked={settings?.hideFace}
              onChange={(e) => handleSettingChange('hideFace', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Profile Visibility</h3>
          <Select
            label="Who can view your profile"
            options={visibilityOptions}
            value={settings?.profileVisibility}
            onChange={(value) => handleSettingChange('profileVisibility', value)}
            description="Control how much information others can see"
          />
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Message Access</h3>
          <Select
            label="Who can message you"
            options={messageAccessOptions}
            value={settings?.messageAccess}
            onChange={(value) => handleSettingChange('messageAccess', value)}
            description="Control who can send you messages"
          />
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Activity Status</h3>
          <div className="space-y-4">
            <Checkbox
              label="Show Online Status"
              description="Let others see when you're online"
              checked={settings?.showOnlineStatus}
              onChange={(e) => handleSettingChange('showOnlineStatus', e?.target?.checked)}
            />

            <Checkbox
              label="Show Last Active"
              description="Display when you were last active"
              checked={settings?.showLastActive}
              onChange={(e) => handleSettingChange('showLastActive', e?.target?.checked)}
            />

            <Checkbox
              label="Allow Profile Views"
              description="Let others know you viewed their profile"
              checked={settings?.allowProfileViews}
              onChange={(e) => handleSettingChange('allowProfileViews', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          fullWidth
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
        >
          Save Privacy Settings
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="RotateCcw"
          iconPosition="left"
          onClick={() => setSettings({
            privacyLevel: privacySettings?.privacyLevel || 'high',
            photoBlur: privacySettings?.photoBlur || true,
            blurIntensity: privacySettings?.blurIntensity || 'high',
            hideFace: privacySettings?.hideFace || false,
            profileVisibility: privacySettings?.profileVisibility || 'anonymous',
            messageAccess: privacySettings?.messageAccess || 'verified-only',
            showOnlineStatus: privacySettings?.showOnlineStatus || false,
            showLastActive: privacySettings?.showLastActive || false,
            allowProfileViews: privacySettings?.allowProfileViews || true
          })}
          disabled={isSaving}
        >
          Reset
        </Button>
      </div>
      {showConfirmation && (
        <div className="fixed bottom-4 right-4 z-[600] animate-slide-up">
          <div className="flex items-center gap-3 px-4 py-3 bg-success text-success-foreground rounded-lg shadow-warm-lg">
            <Icon name="CheckCircle" size={20} color="#FFFFFF" />
            <span className="text-sm font-medium">Privacy settings saved successfully!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyControlsTab;